import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SaveIcon from "@mui/icons-material/Save";
import { Widget4 } from "@solar-icons/react/ssr";
import { Helmet } from "react-helmet";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
const ResponsiveGridLayout = WidthProvider(Responsive);
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { request } from "@/Api";
import { selectPersonId } from "@/auth/authSlice";
import { PERMISSION_GET_PROJECTS } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { PageTitle } from "@/common/CommonComponents";
import { setError } from "@/nav/navSlice";

import DashboardAiPromptBar from "./widgets/DashboardAiPromptBar";
import FinancialSnapshot from "./widgets/FinancialSnapshot";
import {
    WIDGET_REGISTRY,
    renderWidget,
    useWidgetAccess,
} from "./widgetRegistry";
import {
    DEFAULT_LAYOUT,
    COLS,
    ROW_HEIGHT,
    migrateLayoutTo3Col,
    convertLayoutForStorage,
    computeAllLayouts,
    repackLayout,
} from "./layoutUtils";

// ─── Add Widget Popover ─────────────────────────────────────────────────────────

function AddWidgetButton({ layout, onAdd }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const { isAvailable } = useWidgetAccess();

    const activeWidgetIds = layout.map((item) => item.i);
    const availableWidgets = Object.entries(WIDGET_REGISTRY).filter(
        ([id]) => !activeWidgetIds.includes(id) && isAvailable(id),
    );

    if (availableWidgets.length === 0) return null;

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ mr: 1 }}
            >
                Add Widget
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <List sx={{ minWidth: 220, py: 0.5 }}>
                    {availableWidgets.map(([id, widget]) => (
                        <ListItem key={id} disablePadding>
                            <ListItemButton
                                onClick={() => {
                                    onAdd(id);
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    {widget.icon}
                                </ListItemIcon>
                                <ListItemText primary={widget.label} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Popover>
        </>
    );
}

// ─── Time-of-day Greeting ───────────────────────────────────────────────────────

function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
}

// ─── Dashboard Component ────────────────────────────────────────────────────────

export default function Dashboard() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { state } = useLocation(); // used to trigger reload when switching organization

    const [person, setPerson] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);
    const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");
    const savedLayoutRef = useRef(DEFAULT_LAYOUT);
    const scrollAnimRef = useRef(null);

    // Auto-scroll the window when dragging a widget near viewport edges
    const handleDrag = useCallback(
        (layout, oldItem, newItem, placeholder, e) => {
            const scrollZone = 80; // px from edge to start scrolling
            const scrollSpeed = 15;
            const mouseY = e.clientY;
            const viewportH = window.innerHeight;

            if (scrollAnimRef.current) {
                cancelAnimationFrame(scrollAnimRef.current);
                scrollAnimRef.current = null;
            }

            if (mouseY < scrollZone) {
                const tick = () => {
                    window.scrollBy(0, -scrollSpeed);
                    scrollAnimRef.current = requestAnimationFrame(tick);
                };
                scrollAnimRef.current = requestAnimationFrame(tick);
            } else if (mouseY > viewportH - scrollZone) {
                const tick = () => {
                    window.scrollBy(0, scrollSpeed);
                    scrollAnimRef.current = requestAnimationFrame(tick);
                };
                scrollAnimRef.current = requestAnimationFrame(tick);
            }
        },
        [],
    );

    const handleDragStop = useCallback(() => {
        if (scrollAnimRef.current) {
            cancelAnimationFrame(scrollAnimRef.current);
            scrollAnimRef.current = null;
        }
    }, []);

    const personId = useSelector(selectPersonId);
    const roles = useSelector((state) => state.auth.roles);
    const dispatch = useDispatch();
    const { hasPermission } = useEntitlements();
    const canViewProjects = hasPermission(PERMISSION_GET_PROJECTS);
    const { isAvailable } = useWidgetAccess();

    const getDashboard = () => {
        request
            .get("dashboard")
            .then((response) => {
                setPerson(response.data);
                if (response.data.dashboard_layout) {
                    const migrated = migrateLayoutTo3Col(
                        response.data.dashboard_layout,
                    );
                    setLayout(migrated);
                    savedLayoutRef.current = migrated;
                }
            })
            .catch((err) => {
                dispatch(
                    setError({
                        message:
                            err?.response?.data?.detail ||
                            "Failed to load dashboard",
                    }),
                );
            });
    };

    const onLayoutChange = useCallback(
        (_currentLayout, allLayouts) => {
            if (isEditing && allLayouts?.lg) {
                setLayout(allLayouts.lg);
            }
        },
        [isEditing],
    );

    const onBreakpointChange = useCallback((newBreakpoint) => {
        setCurrentBreakpoint(newBreakpoint);
    }, []);

    const isMobile = currentBreakpoint !== "lg" && currentBreakpoint !== "md";

    const availableLayout = useMemo(
        () =>
            layout.filter(
                (item) => WIDGET_REGISTRY[item.i] && isAvailable(item.i),
            ),
        [layout, isAvailable],
    );

    useEffect(() => {
        if (
            availableLayout.length === 0 ||
            availableLayout.length === layout.length
        )
            return;
        const repacked = repackLayout(availableLayout);
        const hasGaps = repacked.some(
            (item, idx) =>
                item.x !== availableLayout[idx].x ||
                item.y !== availableLayout[idx].y,
        );
        if (hasGaps) {
            setLayout(repacked);
        }
    }, [availableLayout.length]);

    const allLayouts = useMemo(
        () => computeAllLayouts(availableLayout),
        [availableLayout],
    );

    const handleSaveLayout = () => {
        const storageLayout = convertLayoutForStorage(layout);
        request
            .put("dashboard/layout", storageLayout)
            .then((response) => {
                if (response.data.dashboard_layout) {
                    const migrated = migrateLayoutTo3Col(
                        response.data.dashboard_layout,
                    );
                    savedLayoutRef.current = migrated;
                    setLayout(migrated);
                } else {
                    savedLayoutRef.current = layout;
                }
                setIsEditing(false);
            })
            .catch((e) => {
                console.error("Failed to save layout", e);
                setIsEditing(false);
            });
    };

    const handleCancelEdit = () => {
        setLayout(savedLayoutRef.current);
        setIsEditing(false);
    };

    const handleAddWidget = (widgetId) => {
        const registryEntry = WIDGET_REGISTRY[widgetId];
        if (!registryEntry) return;

        const newItem = {
            i: widgetId,
            x: 0,
            y: Infinity,
            ...registryEntry.defaultLayout,
        };
        setLayout((prev) => repackLayout([...prev, newItem]));
    };

    const handleRemoveWidget = (widgetId) => {
        setLayout((prev) =>
            repackLayout(prev.filter((item) => item.i !== widgetId)),
        );
    };

    const handleMoveWidget = (widgetId, direction) => {
        setLayout((prev) => {
            const sorted = [...prev].sort((a, b) => a.y - b.y || a.x - b.x);
            const idx = sorted.findIndex((item) => item.i === widgetId);
            if (idx < 0) return prev;
            const swapIdx = idx + direction;
            if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
            [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
            return repackLayout(sorted, undefined, { presorted: true });
        });
    };

    useEffect(() => {
        if (!canViewProjects) {
            navigate("/profile");
        }
        setPerson(null); // needed to trigger re-render of projects section
        getDashboard();
    }, [state]);

    if (!person) {
        return;
    }

    const widgetProps = { personId };

    return (
        <>
            <Helmet>
                <PageTitle title="Dashboard" />
            </Helmet>
            {/* Page header */}
            <Box sx={{ mt: 3, mb: 5 }}>
                <Typography
                    sx={{
                        fontSize: 22,
                        fontWeight: 600,
                        fontFamily: "Inter Tight, sans-serif",
                        color: "text.primary",
                        lineHeight: 1,
                    }}
                >
                    {`${getGreeting()}, ${person.first_name}`}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontFamily: "Inter Tight, sans-serif",
                        color: "text.secondary",
                        mt: 1.5,
                        lineHeight: 1,
                    }}
                >
                    {[
                        roles.length > 0 &&
                            roles
                                .map((r) =>
                                    r
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (c) =>
                                            c.toUpperCase(),
                                        ),
                                )
                                .join(", "),
                        new Date().toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                        }),
                    ]
                        .filter(Boolean)
                        .join("  •  ")}
                </Typography>
            </Box>

            {/* AI Prompt Bar — hidden for now */}
            {/* <Box sx={{ mb: 5 }}>
                <DashboardAiPromptBar />
            </Box> */}

            {/* Key Metrics — hidden for now */}
            {/* <Box sx={{ mb: 5 }}>
                <FinancialSnapshot />
            </Box> */}

            {/* Workbench section header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Widget4
                        size={18}
                        color={theme.palette.text.secondary}
                        weight="Outline"
                    />
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            fontFamily: "Inter Tight, sans-serif",
                            color: "text.primary",
                            lineHeight: 1,
                        }}
                    >
                        Dashboard
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {isEditing && (
                        <>
                            <AddWidgetButton
                                layout={layout}
                                onAdd={handleAddWidget}
                            />
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleCancelEdit}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveLayout}
                            >
                                Save Layout
                            </Button>
                        </>
                    )}
                    {!isEditing && !isMobile && (
                        <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={<EditIcon />}
                            onClick={() => setIsEditing(true)}
                        >
                            Customize
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Override react-grid-layout defaults for dark theme */}
            <style>{`
                .react-grid-item.react-grid-placeholder {
                    background: rgba(255, 255, 255, 0.08) !important;
                    border: 1px dashed rgba(255, 255, 255, 0.3) !important;
                    border-radius: 14px !important;
                    opacity: 1 !important;
                }
                .react-grid-item > .react-resizable-handle {
                    display: none !important;
                }
            `}</style>
            {isEditing && !isMobile && (
                <style>{`
                    .react-grid-item > .react-resizable-handle.react-resizable-handle-e {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        position: absolute !important;
                        width: 16px !important;
                        height: 100% !important;
                        right: -8px !important;
                        top: 0 !important;
                        bottom: 0 !important;
                        margin-top: 0 !important;
                        cursor: ew-resize !important;
                        background: none !important;
                        padding: 0 !important;
                        transform: none !important;
                    }
                    .react-grid-item > .react-resizable-handle.react-resizable-handle-e::after {
                        content: '' !important;
                        display: block !important;
                        width: 6px !important;
                        height: 48px !important;
                        border-radius: 3px !important;
                        border: 1.5px solid rgba(46, 204, 113, 0.7) !important;
                        border-right: 1.5px solid rgba(46, 204, 113, 0.7) !important;
                        border-bottom: 1.5px solid rgba(46, 204, 113, 0.7) !important;
                        background: rgba(46, 204, 113, 0.15) !important;
                        transition: background 0.2s, border-color 0.2s !important;
                        position: static !important;
                        transform: none !important;
                    }
                    .react-grid-item > .react-resizable-handle.react-resizable-handle-e:hover::after {
                        background: rgba(46, 204, 113, 0.35) !important;
                        border-color: rgba(46, 204, 113, 1) !important;
                    }
                `}</style>
            )}
            <ResponsiveGridLayout
                className="layout"
                layouts={allLayouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                margin={[20, 20]}
                containerPadding={[0, 0]}
                onLayoutChange={onLayoutChange}
                onBreakpointChange={onBreakpointChange}
                isDraggable={isEditing && !isMobile}
                isResizable={isEditing && !isMobile}
                compactType="vertical"
                draggableHandle=".widget-drag-handle"
                resizeHandles={["e"]}
                onDrag={handleDrag}
                onDragStop={handleDragStop}
            >
                {availableLayout.map((item) => {
                    const widgetDef = WIDGET_REGISTRY[item.i];

                    return (
                        <div
                            key={item.i}
                            style={{
                                background: theme.palette.background.paper,
                                border: isEditing
                                    ? `1px dashed ${theme.palette.mode === "dark" ? theme.palette.secondary.main : theme.palette.divider}`
                                    : `1px solid ${theme.palette.mode === "dark" ? theme.palette.secondary.main : theme.palette.divider}`,
                                borderRadius: 14,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                position: "relative",
                            }}
                        >
                            {isEditing && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        zIndex: 10,
                                    }}
                                >
                                    {isMobile ? (
                                        <>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleMoveWidget(item.i, -1)
                                                }
                                                sx={{
                                                    color: "text.secondary",
                                                    "&:hover": {
                                                        color: "text.primary",
                                                    },
                                                }}
                                            >
                                                <KeyboardArrowUpIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleMoveWidget(item.i, 1)
                                                }
                                                sx={{
                                                    color: "text.secondary",
                                                    "&:hover": {
                                                        color: "text.primary",
                                                    },
                                                }}
                                            >
                                                <KeyboardArrowDownIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Tooltip title="Drag to move">
                                            <IconButton
                                                size="small"
                                                className="widget-drag-handle"
                                                sx={{
                                                    cursor: "grab",
                                                    color: "text.secondary",
                                                    "&:hover": {
                                                        color: "text.primary",
                                                    },
                                                }}
                                            >
                                                <DragIndicatorIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title={`Remove ${widgetDef.label}`}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleRemoveWidget(item.i)
                                            }
                                            sx={{
                                                color: "text.secondary",
                                                "&:hover": {
                                                    color: "error.main",
                                                },
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            )}
                            <div
                                style={{
                                    flex: 1,
                                    overflow: "auto",
                                    padding: 20,
                                    pointerEvents: isEditing ? "none" : "auto",
                                }}
                            >
                                {renderWidget(item.i, {
                                    ...widgetProps,
                                    gridCols: item.w,
                                })}
                            </div>
                        </div>
                    );
                })}
            </ResponsiveGridLayout>
        </>
    );
}
